import { Component, Input, OnInit } from "@angular/core";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IVorRecord, VorRecordStatus } from "@app/main/components/resourceManagement/vor/vor.interfaces";
import { VorService } from "@app/services/vor.service";
import { VorEditorComponent } from "../vor-editor/vor-editor.component";
import "./vor-vehicle-history.component.scss";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { EndVorCellComponent } from "./end-vor-cell/end-vor-cell.component";
import { TranslationService } from "@app/services/translation.service";
import { DateService } from "@app/services/date.service";

@Component({
    selector: 'vor-vehicle-history',
    template: require("./vor-vehicle-history.component.html")
})
export class VorVehicleHistoryComponent implements OnInit {

    @Input() vehicleId: number;

    // --

    tableColumns: IVTableColumn<IVorRecord>[] = [];
    tableRows: IVTableRow<IVorRecord>[] = [];

    vorRecords: IVorRecord[] = [];
    loading: boolean;
    allowNewRecord = true;

    constructor(
        private readonly vTableService: VTableService,
        private readonly vorService: VorService,
        private readonly vDialogService: DialogService,
        private readonly translationService: TranslationService,
        private readonly dateService: DateService
    ) {
    }

    ngOnInit(): void {
        this.createTableColumns();
    }

    ngOnChanges(): void {
        this.loadVorRecords();
    }

    onTableRowClicked($event: IVTableRow<IVorRecord>): void {
        this.showEditBox($event.data);
    }

    createVorRecord(): void {
        this.showEditBox();
    }

    // ---

    private createTableColumns(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: false }),
                createCellFunction: (record) => this.vTableService.createStatusCell(this.createTableRowStatus(record))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VOR.Description", sortType: SortType.String, sortable: true }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.Description)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "VOR.StartDate", sortType: SortType.String, sortable: true, sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.StartDate)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "VOR.EndDate", sortType: SortType.String, sortable: true }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.EndDate)
            },
            {
                header: this.vTableService.createDateTableHeader({ title: "", width: 100 }  ),
                createCellFunction: (data) => this.createButtonCell( this.translationService.t("Common.End"), data, this.displayEndButton(data))
                
            },
        ]
    }

    private displayEndButton(record: IVorRecord): boolean {
        const now = new Date();
    
        return this.dateService.date1IsAfterDate2(now, record.StartDate) &&
               (record.EndDate == null || this.dateService.date1IsAfterDate2(record.EndDate, now));
    }

    private createButtonCell(label: string, vorRecord: IVorRecord, display: boolean): IVTableCell {
        if (display)
            return this.vTableService.createTableCell(label, { component: EndVorCellComponent, componentInputs: { label: label, vorRecord: vorRecord }, componentOutputs: { endVor: (event: IVorRecord) => this.endVorRecord(event) }, disableCell: true });
        else {
            return this.vTableService.createTableCell("", {}, FallbackCellType.Blank );
        }
    }

    private endVorRecord(vorRecord: IVorRecord): void {
        this.loading = true;
        this.vorService.saveRecord(vorRecord)
            .then(() => {                
                setTimeout(() => { this.loadVorRecords(true); }, 2_000);
                this.allowNewRecord = true;
            });
    }

    private createTableRowStatus(record: IVorRecord): VTableRowStatus {
        switch (record.status) {
            case VorRecordStatus.Active:
                return VTableRowStatus.Warning;

            case VorRecordStatus.Pending:
                return VTableRowStatus.Pending;

            case VorRecordStatus.Completed:
                return VTableRowStatus.Success;
        }
    }

    private loadVorRecords(invalidateCache = false): void {
        this.loading = true;
        this.vorService.getVehicleRecords(this.vehicleId, true, invalidateCache)
            .then(records => {
                this.vorRecords = records;

                this.allowNewRecord = true;
                this.vorRecords.forEach(r => {
                    this.vorService.setVorStatus(r);
                    if (r.status != VorRecordStatus.Completed) {
                        this.allowNewRecord = false;
                    }
                });
            })
            .finally(() => {
                this.tableRows = this.vTableService.createTableRows(this.vorRecords, this.tableColumns);
                this.loading = false;
            });
    }

    private showEditBox(vorRecord?: IVorRecord): Promise<IVorRecord> {

        const p = new Promise<IVorRecord>(resolve => {
            const ref = this.vDialogService.open<IVorRecord>(
                VorEditorComponent,
                {
                    record: vorRecord,
                    vehicleId: this.vehicleId,
                    allRecords: this.vorRecords
                },
                {
                    onSaved: (updatedRecord: IVorRecord) => {
                        resolve(updatedRecord);
                        ref.close();
                    }
                }
            );

            ref.afterClosed$.subscribe(res => {
                resolve(null);
            });
        });

        p.then((refresh) => {
            if (!refresh) {
                return;
            }

            this.loading = true;
            setTimeout(() => { this.loadVorRecords(true); }, 2_000);
        })

        return p;
    }
}
